import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import type { Customer, ApiResponse } from '@/types'
import { removeListQueries, invalidateDetailQueries } from '@/lib/queryHelpers'
import { accountSettingsKeys } from './useAccountSettings'

// Query keys
export const customerKeys = {
  all: ['customers'] as const,
  lists: () => [...customerKeys.all, 'list'] as const,
  list: (filters: Record<string, unknown>) => [...customerKeys.lists(), filters] as const,
  details: () => [...customerKeys.all, 'detail'] as const,
  detail: (id: number) => [...customerKeys.details(), id] as const,
}

// Fetch customers
// NOTE: the controller searches via Meilisearch on params[:q] — `search` was a
// dead-letter param name the controller never read. Existing callers passed
// `search` only for the (now-unused) cycle-mode path; no consumer relied on
// server-side filtering working through this hook. The `q` param is wired below
// for the new CustomerPicker; `search` is kept as an alias that maps to `q` so
// any future direct call stays compatible.
export function useCustomers(
  page = 1,
  status?: string,
  search?: string,
  propertyId?: number,
  // EP-97: server-side sort (allow-listed keys: updated_at/created_at/name).
  sortParams: { sort?: string; sort_order?: string } = {},
) {
  return useQuery({
    queryKey: customerKeys.list({ page, status, search, propertyId, ...sortParams }),
    queryFn: async () => {
      const params: Record<string, unknown> = { page }
      if (status) params.status = status
      // Map the `search` argument to the `q` param the controller actually reads.
      if (search) params.q = search
      if (propertyId) params.property_id = propertyId
      if (sortParams.sort) params.sort = sortParams.sort
      if (sortParams.sort_order) params.sort_order = sortParams.sort_order
      const { data } = await api.get<ApiResponse<Customer[]>>('/api/customers', { params })
      return data
    },
  })
}

// Dedicated server-side search hook for the CustomerPicker combobox.
// Queries `?q=<term>` which the CustomersController routes to Meilisearch
// (blank `q` falls through to the controller's default branch — recent-first,
// Pagy default 20). By default the hook stays disabled while `q` is blank to
// avoid fetching the full customer list on mount; pass `fetchOnEmpty: true`
// to opt a picker into "browse on focus" (e.g. QuickPay), where
// SearchableDropdown already fires `onSearch('')` when it opens.
//
// `page` (EP-116 T005) lets a caller page past the first 20 hits of a broad
// query — the controller's `q` branch already accepts `page` alongside `q`
// (api/app/controllers/api/customers_controller.rb:60-65), so this is wiring
// only. Response `meta` (current_page/total_pages) comes through unchanged
// via ApiResponse, for a caller's `onLoadMore`/`hasMore`.
export function useCustomerSearch(q: string, opts: { fetchOnEmpty?: boolean; page?: number } = {}) {
  const { fetchOnEmpty = false, page = 1 } = opts
  return useQuery({
    queryKey: customerKeys.list({ q, page }),
    queryFn: async () => {
      const params: Record<string, unknown> = { q, page }
      const { data } = await api.get<ApiResponse<Customer[]>>('/api/customers', { params })
      return data
    },
    enabled: fetchOnEmpty || q.trim().length > 0,
  })
}

// Fetch single customer
export function useCustomer(id: number) {
  return useQuery({
    queryKey: customerKeys.detail(id),
    queryFn: async () => {
      const { data } = await api.get<ApiResponse<Customer>>(`/api/customers/${id}`)
      return data.data
    },
    enabled: !!id,
  })
}

// Create customer
interface CreateCustomerInput {
  first_name?: string
  last_name?: string
  phone?: string
  email?: string
  id_card_number?: string
  address?: string
  status?: 'active' | 'alumni' | 'prospect'
  source?: string | null
}

export function useCreateCustomer() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (input: CreateCustomerInput) => {
      const { data } = await api.post<ApiResponse<Customer>>('/api/customers', {
        customer: input,
      })
      return data.data
    },
    onSuccess: () => {
      removeListQueries(queryClient, customerKeys.lists())
    },
  })
}

// Update customer
interface UpdateCustomerInput {
  id: number
  first_name?: string | null
  last_name?: string
  phone?: string | null
  email?: string
  id_card_number?: string
  address?: string
  status?: 'active' | 'alumni' | 'prospect'
  source?: string | null
}

export function useUpdateCustomer() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, ...input }: UpdateCustomerInput) => {
      const { data } = await api.patch<ApiResponse<Customer>>(`/api/customers/${id}`, {
        customer: input,
      })
      return data.data
    },
    onSuccess: (data) => {
      removeListQueries(queryClient, customerKeys.lists())
      invalidateDetailQueries(queryClient, customerKeys.detail(data.id))
    },
  })
}

// Delete customer
export function useDeleteCustomer() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/api/customers/${id}`)
      return id
    },
    onSuccess: () => {
      removeListQueries(queryClient, customerKeys.lists())
      // Refresh per-entity counts so empty-state seed/affordance gating is accurate.
      queryClient.invalidateQueries({ queryKey: accountSettingsKeys.all })
    },
  })
}
